"use client";

import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Download, Upload, FileSpreadsheet, AlertCircle, ArrowLeft, ArrowRight, Plus, X, Search, Check, Loader2, Users, Grid3X3, Shield, BookOpen, Calendar1, Calendar, CheckSquare, MessageCircle, FileText, MessageSquare, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

export function SpacesImportExportModal({ isOpen, onClose, projects, onImport }) {
  const { company, userProfile } = useAuth();
  const fileInputRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [importStep, setImportStep] = useState(1); // 1: Upload, 2: Preview & Map, 3: Access Assignment, 4: Tools Selection
  const [csvData, setCsvData] = useState(null);
  const [columnMappings, setColumnMappings] = useState({});
  const [customFieldNames, setCustomFieldNames] = useState({});
  const [teamMembers, setTeamMembers] = useState([]);
  const [isLoadingTeam, setIsLoadingTeam] = useState(false);
  const [visibilityType, setVisibilityType] = useState('invite_only');
  const [selectedTeamMembers, setSelectedTeamMembers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedTools, setSelectedTools] = useState({
    matrix: true,
    team: true,
    compliance: true,
    qualifications: true,
    bookings: true,
    calendar: true,
    checklists: false,
    checkins: false,
    chat: false,
    docs: false,
    message_board: false
  });

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setImportStep(1);
      setCsvData(null);
      setColumnMappings({});
      setCustomFieldNames({});
      setVisibilityType('invite_only');
      setSelectedTeamMembers([]);
      setSearchQuery('');
      setIsDropdownOpen(false);
      setSelectedTools({
        matrix: true,
        team: true,
        compliance: true,
        qualifications: true,
        bookings: true,
        calendar: true,
        checklists: false,
        checkins: false,
        chat: false,
        docs: false,
        message_board: false
      });
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [isOpen]);

  // Fetch team members when modal opens
  useEffect(() => {
    if (isOpen && company?.id) {
      fetchTeamMembers();
    }
  }, [isOpen, company?.id]);

  const fetchTeamMembers = async () => {
    if (!company?.id) return;

    setIsLoadingTeam(true);
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('id, email, first_name, last_name, avatar_url, company_id, auth_id')
        .eq('company_id', company.id)
        .not('auth_id', 'is', null);

      if (error) throw error;

      const formattedMembers = data?.map(member => ({
        id: member.id,
        name: `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email,
        email: member.email,
        role: member.id === userProfile?.id ? '(You)' : '',
        avatar: member.avatar_url || null,
        initials: `${(member.first_name || member.email)[0]}${(member.last_name || '')[0] || ''}`.toUpperCase(),
        isCurrentUser: member.id === userProfile?.id
      })) || [];

      setTeamMembers(formattedMembers);
    } catch (error) {
      console.error('Error fetching team members:', error);
    } finally {
      setIsLoadingTeam(false);
    }
  };

  const handleTeamMemberToggle = (member) => {
    setSelectedTeamMembers(prev =>
      prev.find(m => m.id === member.id)
        ? prev.filter(m => m.id !== member.id)
        : [...prev, member]
    );
  };

  // Standard fields that can be mapped (removed visibility_type)
  const standardFields = [
    { value: "_unmapped", label: "Select column..." },
    { value: "name", label: "Name (Required)" },
    { value: "type", label: "Type" },
    { value: "description", label: "Description" },
    { value: "location", label: "Location" },
    { value: "industry", label: "Industry" },
    { value: "matrix_name", label: "Matrix Name" },
    { value: "_ignore", label: "Ignore" },
    { value: "_custom", label: "Custom Field..." }
  ];

  // Export all spaces to CSV
  const handleExport = () => {
    setExporting(true);

    try {
      // Collect all fields including custom ones
      const allFieldsSet = new Set(['Name', 'Type', 'Description', 'Location', 'Industry', 'Matrix Name']);

      // Add custom fields from all projects
      projects.forEach(project => {
        if (project.custom_fields) {
          Object.keys(project.custom_fields).forEach(key => allFieldsSet.add(key));
        }
      });

      const headers = Array.from(allFieldsSet);

      // Convert projects to CSV rows
      const rows = projects.map(project => {
        return headers.map(header => {
          switch(header) {
            case 'Name': return project.name || '';
            case 'Type': return project.type || '';
            case 'Description': return project.description || '';
            case 'Location': return project.location || '';
            case 'Industry': return project.industry || '';
            case 'Matrix Name': return project.matrix_name || '';
            default:
              // Custom field
              return project.custom_fields?.[header] || '';
          }
        });
      });

      // Combine headers and rows
      const csvContent = [
        headers.join(','),
        ...rows.map(row =>
          row.map(field => {
            // Escape quotes and wrap in quotes if contains comma, quote, or newline
            const escaped = String(field).replace(/"/g, '""');
            return /[,"\n]/.test(escaped) ? `"${escaped}"` : escaped;
          }).join(',')
        )
      ].join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);

      link.setAttribute('href', url);
      link.setAttribute('download', `spaces-export-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Exported ${projects.length} spaces successfully`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export spaces');
    } finally {
      setExporting(false);
    }
  };

  // Download template CSV
  const handleDownloadTemplate = () => {
    const headers = [
      'Name',
      'Type',
      'Description',
      'Location',
      'Industry',
      'Matrix Name'
    ];

    const exampleRow = [
      'Example Space',
      'Project',
      'This is an example space description',
      'London, UK',
      'Construction',
      'Training Matrix'
    ];

    const csvContent = [
      headers.join(','),
      exampleRow.map(field => {
        const escaped = String(field).replace(/"/g, '""');
        return /[,"\n]/.test(escaped) ? `"${escaped}"` : escaped;
      }).join(',')
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', 'spaces-import-template.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success('Template downloaded successfully');
  };

  // Parse CSV line handling quotes properly
  const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          current += '"';
          i++; // Skip next quote
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }

    result.push(current);
    return result.map(field => field.replace(/^"|"$/g, ''));
  };

  // Handle file upload and parse CSV
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const lines = text.split('\n').filter(line => line.trim());

      if (lines.length < 2) {
        toast.error('File is empty or invalid');
        // Reset file input so user can try again
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }

      // Parse headers and data
      const headers = parseCSVLine(lines[0]);
      const dataRows = [];

      for (let i = 1; i < Math.min(lines.length, 11); i++) { // Preview first 10 rows
        const values = parseCSVLine(lines[i]);
        if (values.length === headers.length) {
          dataRows.push(values);
        }
      }

      // Auto-map columns based on header names
      const autoMappings = {};
      headers.forEach((header, index) => {
        const normalized = header.toLowerCase().trim();
        if (normalized === 'name') autoMappings[index] = 'name';
        else if (normalized === 'type') autoMappings[index] = 'type';
        else if (normalized === 'description') autoMappings[index] = 'description';
        else if (normalized === 'location') autoMappings[index] = 'location';
        else if (normalized === 'industry') autoMappings[index] = 'industry';
        else if (normalized.includes('matrix')) autoMappings[index] = 'matrix_name';
        else autoMappings[index] = '_unmapped';
      });

      setCsvData({
        headers,
        dataRows,
        allLines: lines,
        totalRows: lines.length - 1
      });
      setColumnMappings(autoMappings);
      setImportStep(2);

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      console.error('File parse error:', error);
      toast.error('Failed to parse CSV file');
      // Reset file input so user can try again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Handle column mapping change
  const handleMappingChange = (columnIndex, value) => {
    setColumnMappings(prev => ({
      ...prev,
      [columnIndex]: value
    }));

    // If switching to custom, pre-populate with the CSV column header name
    if (value === '_custom' && csvData?.headers?.[columnIndex]) {
      setCustomFieldNames(prev => ({
        ...prev,
        [columnIndex]: csvData.headers[columnIndex]
      }));
    }
    // If switching away from custom, remove the custom field name
    else if (value !== '_custom' && customFieldNames[columnIndex]) {
      setCustomFieldNames(prev => {
        const newNames = { ...prev };
        delete newNames[columnIndex];
        return newNames;
      });
    }
  };

  // Handle custom field name change
  const handleCustomFieldNameChange = (columnIndex, name) => {
    setCustomFieldNames(prev => ({
      ...prev,
      [columnIndex]: name
    }));
  };

  // Perform the actual import
  // Move from step 2 to step 3 (access assignment)
  const handleProceedToAccess = () => {
    // Check if Name is mapped
    const hasNameMapping = Object.values(columnMappings).includes('name');
    if (!hasNameMapping) {
      toast.error('You must map at least one column to "Name"');
      return;
    }

    // Move to access assignment step
    setImportStep(3);
  };

  // Final import with access assignment
  const handleFinalImport = async (skipAccess = false) => {
    setImporting(true);

    try {
      const spaces = [];

      // Process all data rows (skip header)
      for (let i = 1; i < csvData.allLines.length; i++) {
        const values = parseCSVLine(csvData.allLines[i]);

        if (values.length !== csvData.headers.length) {
          console.warn(`Row ${i + 1} has incorrect number of columns, skipping`);
          continue;
        }

        const space = {
          custom_fields: {}
        };

        // Apply mappings
        values.forEach((value, columnIndex) => {
          const mapping = columnMappings[columnIndex];

          if (!mapping || mapping === '_ignore' || mapping === '_unmapped') {
            return;
          }

          if (mapping === '_custom') {
            // Custom field
            const customFieldName = customFieldNames[columnIndex];
            if (customFieldName) {
              space.custom_fields[customFieldName] = value?.trim() || '';
            }
          } else {
            // Standard field
            space[mapping] = value?.trim() || '';
          }
        });

        // Validate required fields
        if (!space.name) {
          console.warn(`Row ${i + 1} missing required Name field, skipping`);
          continue;
        }

        // Set defaults
        if (!space.type) space.type = 'Project';

        // Set visibility based on access assignment choice
        if (!skipAccess) {
          space.visibility_type = visibilityType;
          // Set proper roles for team members (valid roles: Owner, Member, Viewer)
          const membersWithRoles = selectedTeamMembers.map(member => ({
            id: member.id,
            role: 'Member'
          }));
          space.teamMembers = visibilityType === 'invite_only'
            ? [...membersWithRoles, { id: userProfile?.id, role: 'Owner' }]
            : [];
        } else {
          space.visibility_type = 'invite_only';
          space.teamMembers = [{ id: userProfile?.id, role: 'Owner' }];
        }

        // Add selected tools to the space
        space.tools = selectedTools;

        spaces.push(space);
      }

      if (spaces.length === 0) {
        toast.error('No valid spaces found in file');
        setImporting(false);
        return;
      }

      // Call the import handler
      await onImport(spaces);

      toast.success(`Successfully imported ${spaces.length} space${spaces.length !== 1 ? 's' : ''}`);
      onClose();
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import spaces: ' + error.message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            layout
            className={`${importStep === 2 ? 'w-[90vw]' : importStep === 4 ? 'w-full max-w-4xl' : 'w-full max-w-2xl'} bg-white rounded-lg shadow-xl my-8 flex flex-col`}
            onClick={(e) => e.stopPropagation()}
          >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-lg font-semibold">Import / Export Spaces</h2>
          <button
            onClick={onClose}
            className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 p-6">
        <Tabs defaultValue="export" className="w-full flex flex-col">
          <TabsList className="grid w-full grid-cols-2 flex-shrink-0">
            <TabsTrigger value="export">Export</TabsTrigger>
            <TabsTrigger value="import" onClick={() => setImportStep(1)}>Import</TabsTrigger>
          </TabsList>

          {/* Export Tab */}
          <TabsContent value="export" className="space-y-4 flex-1 overflow-y-auto">
            <div className="border rounded-lg p-6 space-y-4">
              <div className="flex items-start gap-3">
                <Download className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold mb-1">Export All Spaces</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Download all your spaces as a CSV file. This includes all standard fields and any custom fields you've added.
                  </p>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">
                      {projects.length} space{projects.length !== 1 ? 's' : ''} will be exported
                    </p>
                    <Button
                      onClick={handleExport}
                      disabled={exporting || projects.length === 0}
                      className="gap-2"
                    >
                      <Download className="h-4 w-4" />
                      {exporting ? 'Exporting...' : 'Export Spaces'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Import Tab */}
          <TabsContent value="import" className="space-y-4 flex-1 flex flex-col">
            {importStep === 1 && (
              <>
                {/* Download Template */}
                <div className="border rounded-lg p-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <FileSpreadsheet className="h-5 w-5 text-green-600 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="font-semibold mb-1">Download Template</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Download a template CSV file with the standard fields. You can add your own custom columns too!
                      </p>
                      <Button
                        onClick={handleDownloadTemplate}
                        variant="outline"
                        className="gap-2"
                      >
                        <Download className="h-4 w-4" />
                        Download Template
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Upload File */}
                <div className="border rounded-lg p-6 space-y-4">
                  <div className="flex items-start gap-3">
                    <Upload className="h-5 w-5 text-purple-600 mt-0.5" />
                    <div className="flex-1">
                      <h3 className="font-semibold mb-1">Upload CSV File</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Upload your CSV file. Don't worry about column names - you'll be able to map them in the next step!
                      </p>

                      <input
                        ref={fileInputRef}
                        type="file"
                        id="csv-upload"
                        accept=".csv"
                        onChange={handleFileUpload}
                        className="hidden"
                      />
                      <label htmlFor="csv-upload">
                        <Button
                          asChild
                          className="gap-2"
                        >
                          <span>
                            <Upload className="h-4 w-4" />
                            Choose CSV File
                          </span>
                        </Button>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Import Info */}
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex gap-3">
                    <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
                    <div className="space-y-2 text-sm">
                      <p className="font-medium text-blue-900">New Improved Import:</p>
                      <ul className="list-disc list-inside space-y-1 text-blue-800">
                        <li>Upload any CSV file with any column names</li>
                        <li>Map columns to space fields in the next step</li>
                        <li>Create custom fields for additional data</li>
                        <li>Preview your data before importing</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </>
            )}

            {importStep === 2 && csvData && (
              <>
                {/* Header Info */}
                <div className="space-y-2 flex-shrink-0">
                  <h3 className="font-semibold">Map Your Columns</h3>
                  <p className="text-sm text-muted-foreground">
                    Use the dropdowns in the column headers to map your CSV columns to space fields.
                    <span className="block mt-1 font-medium text-orange-600">At least one column must be mapped to "Name"</span>
                  </p>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-sm text-green-800">
                      <strong>Preview:</strong> Showing first {csvData.dataRows.length} of {csvData.totalRows} row{csvData.totalRows !== 1 ? 's' : ''} that will be imported
                    </p>
                  </div>
                </div>

                {/* Table View */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                      <thead className="bg-gray-50 sticky top-0 z-10">
                        <tr>
                          {csvData.headers.map((header, columnIndex) => (
                            <th key={columnIndex} className="border-b border-r p-2 min-w-[180px] max-w-[250px] bg-gray-50">
                              <div className="space-y-2">
                                {/* Original column name */}
                                <div className="text-xs font-medium text-gray-500 truncate" title={header}>
                                  CSV: {header}
                                </div>

                                {/* Show input if custom field is selected, otherwise show dropdown */}
                                {columnMappings[columnIndex] === '_custom' ? (
                                  <div className="relative">
                                    <Input
                                      placeholder="Enter custom field name..."
                                      value={customFieldNames[columnIndex] || ''}
                                      onChange={(e) => handleCustomFieldNameChange(columnIndex, e.target.value)}
                                      className="w-full h-8 text-xs pr-8"
                                      autoFocus
                                    />
                                    <button
                                      onClick={() => {
                                        setColumnMappings(prev => ({
                                          ...prev,
                                          [columnIndex]: '_unmapped'
                                        }));
                                        setCustomFieldNames(prev => {
                                          const newNames = { ...prev };
                                          delete newNames[columnIndex];
                                          return newNames;
                                        });
                                      }}
                                      className="absolute right-1 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
                                      title="Cancel custom field"
                                    >
                                      <X className="h-3 w-3 text-gray-500" />
                                    </button>
                                  </div>
                                ) : (
                                  <Select
                                    value={columnMappings[columnIndex] || '_unmapped'}
                                    onValueChange={(value) => handleMappingChange(columnIndex, value)}
                                  >
                                    <SelectTrigger className="w-full h-8 text-xs bg-white">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {standardFields.map(field => (
                                        <SelectItem key={field.value} value={field.value}>
                                          {field.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                )}
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {csvData.dataRows.map((row, rowIndex) => (
                          <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                            {row.map((cell, cellIndex) => (
                              <td key={cellIndex} className="border-b border-r p-2 text-sm text-gray-700">
                                <div className="max-w-[250px] truncate" title={cell}>
                                  {cell || <span className="text-gray-400 italic">empty</span>}
                                </div>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex justify-between pt-4 border-t flex-shrink-0">
                  <Button
                    variant="outline"
                    onClick={() => setImportStep(1)}
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={handleProceedToAccess}
                    disabled={!Object.values(columnMappings).includes('name')}
                  >
                    Next
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </>
            )}

            {/* Step 3: Assign Access */}
            {importStep === 3 && (
              <>
                <div className="space-y-4">
                  <div className="text-center mb-4">
                    <h3 className="text-xl font-semibold">Assign Access to All Spaces</h3>
                    <p className="text-sm text-muted-foreground">
                      Choose who can access the imported spaces
                    </p>
                  </div>

                  {/* Visibility Toggle */}
                  <RadioGroup
                    value={visibilityType}
                    onValueChange={setVisibilityType}
                    className="bg-white border rounded-lg p-4 space-y-4 mb-6"
                  >
                    <div className="flex items-start space-x-3">
                      <RadioGroupItem value="company_wide" id="company_wide" className="mt-1" />
                      <label htmlFor="company_wide" className="cursor-pointer">
                        <p className="font-medium">All company members</p>
                        <p className="text-sm text-muted-foreground">Anyone in {company?.name || 'your company'} can access these spaces</p>
                      </label>
                    </div>

                    <div className="flex items-start space-x-3">
                      <RadioGroupItem value="invite_only" id="invite_only" className="mt-1" />
                      <label htmlFor="invite_only" className="cursor-pointer">
                        <p className="font-medium">Invite only</p>
                        <p className="text-sm text-muted-foreground">Only selected team members can access these spaces</p>
                      </label>
                    </div>
                  </RadioGroup>

                  {/* Team member selection - only show if invite_only */}
                  {visibilityType === 'invite_only' ? (
                    <div className="space-y-4">
                      <Label className="text-base">Select team members to invite</Label>

                      {/* Search Input with Dropdown */}
                      <div className="relative">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                          <Input
                            type="text"
                            placeholder="Search team members..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            onFocus={() => setIsDropdownOpen(true)}
                            className="pl-10 pr-4"
                          />
                        </div>

                        {/* Selected Members Count */}
                        {selectedTeamMembers.length > 0 && (
                          <div className="mt-2">
                            <p className="text-sm text-muted-foreground">
                              {selectedTeamMembers.length} member{selectedTeamMembers.length !== 1 ? 's' : ''} selected
                            </p>
                          </div>
                        )}

                        {/* Dropdown List */}
                        <AnimatePresence>
                        {isDropdownOpen && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3, ease: "easeInOut" }}
                            className="w-full mt-1 bg-white border rounded-lg shadow-lg overflow-hidden"
                          >
                            {isLoadingTeam ? (
                              <div className="flex items-center justify-center py-8">
                                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                                <span className="ml-2 text-sm text-muted-foreground">Loading team members...</span>
                              </div>
                            ) : (
                              <ScrollArea className="h-[200px]">
                                <div className="p-2">
                                  {teamMembers
                                    .filter(member => {
                                      const query = searchQuery.toLowerCase();
                                      return member.name.toLowerCase().includes(query) ||
                                             member.email.toLowerCase().includes(query);
                                    })
                                    .map((member) => {
                                      const isCreator = member.id === userProfile?.id;
                                      const isSelected = selectedTeamMembers.some(m => m.id === member.id);

                                      return (
                                        <div
                                          key={member.id}
                                          onClick={() => !isCreator && handleTeamMemberToggle(member)}
                                          className={`flex items-center space-x-3 p-2 rounded-lg cursor-pointer hover:bg-gray-50 ${
                                            isCreator ? 'opacity-50 cursor-not-allowed' : ''
                                          }`}
                                        >
                                          <div className="flex items-center justify-center w-5 h-5">
                                            {(isSelected || isCreator) && (
                                              <Check className="h-4 w-4 text-green-600" />
                                            )}
                                          </div>
                                          <Avatar className="h-8 w-8">
                                            <AvatarImage src={member.avatar} alt={member.name} />
                                            <AvatarFallback className="text-xs">{member.initials}</AvatarFallback>
                                          </Avatar>
                                          <div className="flex-1 min-w-0">
                                            <p className="font-medium truncate">
                                              {member.name}
                                              {isCreator && <span className="ml-2 text-xs text-green-600 font-medium">(You - Owner)</span>}
                                            </p>
                                            <p className="text-sm text-muted-foreground truncate">{member.email}</p>
                                          </div>
                                        </div>
                                      );
                                    })
                                  }
                                  {teamMembers.filter(member => {
                                    const query = searchQuery.toLowerCase();
                                    return member.name.toLowerCase().includes(query) ||
                                           member.email.toLowerCase().includes(query);
                                  }).length === 0 && (
                                    <div className="text-center py-4">
                                      <p className="text-sm text-muted-foreground">No members found matching "{searchQuery}"</p>
                                    </div>
                                  )}
                                </div>
                              </ScrollArea>
                            )}
                            <div className="border-t p-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="w-full"
                                onClick={() => setIsDropdownOpen(false)}
                              >
                                Done
                              </Button>
                            </div>
                          </motion.div>
                        )}
                        </AnimatePresence>
                      </div>

                      {/* Selected Members Display */}
                      {selectedTeamMembers.length > 0 && (
                        <div className="space-y-2">
                          <Label className="text-sm text-muted-foreground">Selected members:</Label>
                          <div className="flex flex-wrap gap-2">
                            {selectedTeamMembers.map((member) => (
                              <Badge key={member.id} variant="secondary" className="pl-2 pr-1 py-1">
                                <span className="mr-1">{member.name}</span>
                                <button
                                  onClick={() => handleTeamMemberToggle(member)}
                                  className="ml-1 rounded-full hover:bg-gray-300 p-0.5"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="pt-2">
                        <p className="text-sm text-muted-foreground">These members will be added to all imported spaces.</p>
                      </div>
                    </div>
                  ) : (
                    <Alert className="bg-blue-50 border-blue-200">
                      <AlertDescription className="text-blue-800">
                        <strong>Company-wide access enabled</strong><br/>
                        All current and future members of {company?.name || 'your company'} will automatically have access to these spaces.
                      </AlertDescription>
                    </Alert>
                  )}
                </div>

                {/* Navigation */}
                <div className="flex justify-between pt-4 border-t flex-shrink-0">
                  <Button
                    variant="outline"
                    onClick={() => setImportStep(2)}
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={() => setImportStep(4)}
                  >
                    Continue
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              </>
            )}

            {/* Step 4: Select Tools */}
            {importStep === 4 && (
              <>
                <div className="space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-left">
                      <h3 className="text-xl font-semibold">Select Tools for All Spaces</h3>
                      <p className="text-sm text-muted-foreground">
                        Choose which tools to enable for the imported spaces
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const allEnabled = Object.values(selectedTools).every(v => v);
                        const newState = {};
                        Object.keys(selectedTools).forEach(key => {
                          newState[key] = !allEnabled;
                        });
                        setSelectedTools(newState);
                      }}
                    >
                      {Object.values(selectedTools).every(v => v) ? 'Disable All' : 'Enable All'}
                    </Button>
                  </div>

                  {/* Tools Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      {
                        id: 'matrix',
                        name: 'Matrix',
                        icon: Grid3X3,
                        description: 'Create and manage training competency matrices'
                      },
                      {
                        id: 'team',
                        name: 'Team',
                        icon: Users,
                        description: 'Manage team members, roles, and responsibilities'
                      },
                      {
                        id: 'compliance',
                        name: 'AI Compliance',
                        icon: Shield,
                        description: 'AI-powered compliance tracking tool'
                      },
                      {
                        id: 'qualifications',
                        name: 'Qualifications',
                        icon: BookOpen,
                        description: 'Manage training requirements and certifications'
                      },
                      {
                        id: 'bookings',
                        name: 'Bookings',
                        icon: Calendar1,
                        description: 'Schedule and manage training sessions'
                      },
                      {
                        id: 'calendar',
                        name: 'Calendar',
                        icon: Calendar,
                        description: 'Set important dates and deadlines'
                      },
                      {
                        id: 'checklists',
                        name: 'Checklists',
                        icon: CheckSquare,
                        description: 'Create task lists for training completion'
                      },
                      {
                        id: 'checkins',
                        name: 'Check-ins',
                        icon: CheckCircle2,
                        description: 'Regular team check-ins and status updates'
                      },
                      {
                        id: 'chat',
                        name: 'Chat',
                        icon: MessageCircle,
                        description: 'Real-time team communication'
                      },
                      {
                        id: 'docs',
                        name: 'Docs & Files',
                        icon: FileText,
                        description: 'Store and collaborate on documents'
                      },
                      {
                        id: 'message_board',
                        name: 'Posts',
                        icon: MessageSquare,
                        description: 'Post announcements and updates'
                      }
                    ].map((tool) => {
                      const Icon = tool.icon;
                      const isSelected = selectedTools[tool.id];

                      return (
                        <motion.div
                          key={tool.id}
                          className={`border rounded-lg p-4 cursor-pointer transition-all ${
                            isSelected
                              ? 'bg-blue-50 border-blue-300 dark:bg-blue-900/20 dark:border-blue-700'
                              : 'bg-white hover:bg-gray-50 border-gray-200'
                          }`}
                          onClick={() => setSelectedTools(prev => ({ ...prev, [tool.id]: !prev[tool.id] }))}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <div className="flex items-start space-x-3">
                            <div className={`flex items-center justify-center w-5 h-5 rounded border-2 flex-shrink-0 mt-0.5 ${
                              isSelected
                                ? 'bg-blue-600 border-blue-600'
                                : 'border-gray-300'
                            }`}>
                              {isSelected && (
                                <Check className="h-3 w-3 text-white" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center space-x-2">
                                <Icon className={`h-4 w-4 ${isSelected ? 'text-blue-600' : 'text-gray-500'}`} />
                                <p className={`font-medium ${isSelected ? 'text-blue-900 dark:text-blue-100' : 'text-gray-900'}`}>
                                  {tool.name}
                                </p>
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">{tool.description}</p>
                            </div>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>

                  <div className="pt-2">
                    <p className="text-sm text-muted-foreground">
                      Selected tools will be enabled for all imported spaces. You can customize tools for individual spaces later.
                    </p>
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex justify-between pt-4 border-t flex-shrink-0">
                  <Button
                    variant="outline"
                    onClick={() => setImportStep(3)}
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => handleFinalImport(true)}
                      disabled={importing}
                    >
                      {importing ? 'Importing...' : 'Skip and Import'}
                    </Button>
                    <Button
                      onClick={() => handleFinalImport(false)}
                      disabled={importing}
                    >
                      {importing ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        <>
                          Import Spaces
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </TabsContent>
        </Tabs>
        </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
